import { createContext, useState, useEffect, useContext } from 'react';
import { useAPI } from 'src/services/api';
import AuthContext from 'src/store/auth-context';
import debounce from 'src/util/debounce';
import shouldBypassItemization from 'src/util/shouldBypassItemization';
export const AppContext = createContext();
export const AppProvider = ({ children }) => {
  const api = useAPI();
  const { isLoggedIn, account } = useContext(AuthContext);
  const [shouldSkipToLastStep, setShouldSkipToLastStep] = useState(false);
  const [lastActive, setLastActive] = useState(false);

  // Stores the path for redirecting upon auto-logout, used in LoginRequiredRoute.
  // It determines the appropriate login page (/admin/login or /login) based on the user's status.
  const [autoLogoutPath, setAutoLogoutPath] = useState('');

  useEffect(() => {
    const initalizeSkiptToLastStep = async () => {
      try {
        const response = await api.get('/filing?fields=BUS_STATUS,DATE_BUS_SOLD,DATE_BUS_CLOSED');
        const data = response.data;
        setShouldSkipToLastStep(shouldBypassItemization(account, data));
      } catch (err) {
        console.error(err);
      }
    };
    if (isLoggedIn) initalizeSkiptToLastStep();
  }, [api, isLoggedIn, account]);

  useEffect(() => {
    const handleUserActivity = () => {
      // Update the state to reflect the most recent activity timestamp
      setLastActive(new Date());
    };

    // Debounce delay is set to 250 milliseconds
    const debouncedHandleUserActivity = debounce(handleUserActivity, 250);

    // Define a list of events considered as user activity
    // These events are monitored to determine if the user is active
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

    activityEvents.forEach((event) => {
      window.addEventListener(event, debouncedHandleUserActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, debouncedHandleUserActivity);
      });
    };
  }, []);

  return (
    <AppContext.Provider
      value={{
        shouldSkipToLastStep,
        setShouldSkipToLastStep,
        lastActive,
        autoLogoutPath,
        setAutoLogoutPath,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
